import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {IntegrationAdAccountList, IntegrationAdAccountRow} from "./IntegrationAdAccounts";
import {Box, Button, DialogActions, DialogContent, List, ListItem, StepConnector, useMediaQuery, useTheme, ListItemIcon, ListItemText, Step, StepLabel, Stepper, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {integrationService} from "../../../service/integrationService";
import {alertActions} from "../../../store/actions/alertActions";
import useCustomStyles from "../../utils/UseCustomStyles";

const customStyles = (theme) => ({
    chooseAccount: {
        fontWeight: 500,
        fontSize: 14,
        paddingTop: 24,
        paddingBottom: 4
    },
    listItem: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingBottom: 0
    },
    listItemIcon: {
        minWidth: 42
    },
    stepper: {
        margin: "0 auto",
        width: "100%",
        maxWidth: 800,
        padding: "8px 24px 24px 24px"
    },
    stepConnectorLine: {
        borderColor: theme.palette.background.tableBorder
    },
});

function TikTokAdAccountIntegration(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {
        integration,
        getTikTokAdAccounts,
        integrateTikTokAdAccount,
        resetIntegration,
        integrateTikTokConversionAPI,
        error,
        removeIntegrationEvent
    } = props;

    const steps = ['Connect', 'Integrate Account', 'Conversion API'];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [removePixelIndex, setRemovePixelIndex] = useState(null);

    const {register, handleSubmit, control, watch, setValue, getValues, formState: {errors}} = useForm();
    const {fields: eventFields, remove: eventRemove, append: eventAppend} = useFieldArray(
        {control, name: "events"}
    );
    const {fields: pixelFields, remove: pixelRemove, append: pixelAppend} = useFieldArray(
        {control, name: "pixels"}
    );

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props]);

    const handleOnSubmit = (data) => {
        data.integrationId = integrationId;

        let pixels = [];
        data?.pixels?.forEach((pixel) => {
            pixels?.push({
                id: pixel?.rowId,
                name: pixel?.name,
                pixelId: pixel?.pixelId,
                pixelAccessToken: pixel?.pixelAccessToken
            })
        });
        data.pixels = pixels;

        integrateTikTokConversionAPI(data);
        handleClose();
    };

    const appendNewEvent = () => {
        eventAppend({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            eventRemove(index); // from list
            return false;
        }
        setRemoveEventIndex({eventId, index}); // set event to remove from list and do API req.
    }, [eventRemove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            eventRemove(removeEventIndex?.index); // from list
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId}); // API req
        }
    }, [integrationId, eventRemove, removeEventIndex, removeIntegrationEvent]);

    const appendNewPixel = () => {
        pixelAppend({
            rowId: 0,
            name: "",
            pixelId: "",
            pixelAccessToken: ""
        });
    };

    const handleDeleteIntegrationPixel = useCallback(({pixelId, index}) => {
        if (pixelId === 0) {
            pixelRemove(index);
            return false;
        }

        integrationService.deleteIntegrationPixel({integrationId, pixelId}).then(() => {
            setRemovePixelIndex(index);
        }).catch((e) => {
            error(e?.response?.data?.error);
        });
    }, [integrationId, error, pixelRemove]);

    useEffect(() => {
        if (removePixelIndex !== null) {
            pixelRemove(removePixelIndex);
        }
    }, [pixelRemove, removePixelIndex])

    useEffect(() => {
        if (integration.adAccounts === null) {
            getTikTokAdAccounts(integrationId);
        }
    }, [integrationId, getTikTokAdAccounts, integration.adAccounts])

    useEffect(() => {
        if (activeStep === 2 && integration?.integrations !== null) {
            for (const i of integration?.integrations) {
                if (i?.id === integrationId) {
                    for (const cred of i?.integrationCredentials) {
                        if (cred?.keyName === "tt_enable_conversion_api") {
                            if (cred?.keyValue === "true") {
                                setValue("enableConversionAPI", true);
                            } else {
                                setValue("enableConversionAPI", false);
                            }
                        }
                    }

                    if (getValues("events")?.length === 0) {
                        let events = [];
                        i?.integrationEvents?.forEach((event) => {
                            events.push({
                                eventId: event?.id,
                                name: event?.name,
                                includePayout: event?.includePayout
                            });
                        });
                        setValue("events", events);
                    }

                    let pixels = [];
                    i?.integrationPixels?.forEach((pixel) => {
                        pixels.push({
                            rowId: pixel?.id,
                            name: pixel?.name,
                            pixelId: pixel?.pixelId,
                            pixelAccessToken: pixel?.pixelAccessToken,
                        });
                    });
                    setValue("pixels", pixels);
                    break;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep])

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="tiktok-ad-account-integration"
        >
            <IntegrationHeader name="TikTok" logo="/assets/images/traffic-sources/tiktokwhite.png" id="tiktok-ad-account-integration"/>
            <DialogContent>

                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep}
                    connector={<StepConnector classes={{line: classes?.stepConnectorLine}}/>}
                    classes={{root: classes?.stepper}}
                >
                    {steps?.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <List>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Send conversions to Tiktok to feed their AI and optimize audiences."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Sync ads spent every 30 minutes to Skro, so you get accurate reporting."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Check the delivery status and manage the status of Campaigns, AdGroups and Ads directly from Skro."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Change Daily Budget for Campaigns and AdGroups directly from Skro."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Import dynamic columns like CPM, Link CPC, Link CTR,
                                              Impressions, Link EPC and Link Clicks from Tiktok to Skro."
                                />
                            </ListItem>
                        </List>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/tiktok-integration"
                        >here</Link>.
                        </p>

                        <div className={classes?.chooseAccount}>Tiktok Ad Accounts</div>
                        {
                            integration?.loading?.adAccounts ? (
                                <Spinner overComponentBox={true}/>
                            ) : (
                                <IntegrationAdAccountList provider="TikTok">
                                    {integration?.adAccounts?.map((item) => {
                                        return (
                                            <IntegrationAdAccountRow key={item?.accountId} name={item?.name}
                                                accountId={item?.accountId} integrated={item?.integrated}
                                                onIntegrate={() => integrateTikTokAdAccount(item?.accountId, item?.name, integrationId)}/>
                                        );
                                    })}
                                </IntegrationAdAccountList>
                            )
                        }
                    </>
                }

                {activeStep === 2 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="TikTok" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={eventFields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent} eventLimit={20}
                            pixelFields={pixelFields} onAddPixel={appendNewPixel} onDeletePixel={handleDeleteIntegrationPixel}
                            initializePixels/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={() => setActiveStep(2)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert,
        auth: state.auth
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    getTikTokAdAccounts: integrationActions.getTikTokAdAccounts,
    integrateTikTokAdAccount: integrationActions.integrateTikTokAdAccount,
    integrateTikTokConversionAPI: integrationActions.integrateTikTokConversionAPI,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(TikTokAdAccountIntegration);
